import { useState } from 'react';
import PropTypes from 'prop-types';
import { Datagrid, DateField, FunctionField, ReferenceField, usePermissions, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';
import SimpleList from '@/components/global/list/SimpleList.jsx';
import { CustomDatagridBody } from '@/components/global/list/CustomDatagridRow.jsx';
import CollapsibleSection from '@/components/global/fields/CollapsibleSection.jsx';

const StyledChangeHistory = styled(Box)(() => ({
    '& .MuiCollapse-root.MuiCollapse-entered, & .MuiCollapse-entered .MuiCollapse-wrapper, & .MuiCollapse-entered .MuiCollapse-wrapperInner': {
        overflow: 'visible',
    },
    '& .device-details-collapse': {
        minWidth: 0,
        '& > div': {
            minWidth: 0,
            maxWidth: '100%',
        },
    },
    '& .scroll-container': {
        overflow: 'visible',
        minWidth: 0,
        maxWidth: '100%',
    },
    '& .RaDatagrid-root .RaDatagrid-tableWrapper': {
        marginLeft: 0,
        marginRight: 0,
        maxWidth: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
    },
    '& .scroll-container .RaDatagrid-table': {
        overflow: 'visible',
        '& .MuiTableRow-head': {
            boxShadow: 'none !important',
        },
    },
    '& .column-changes': {
        whiteSpace: 'normal',
        minWidth: '240px',
    },
}));

const formatChangeValue = (value, translate) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    if (typeof value === 'boolean') {
        return translate(value ? 'common.boolean.yes' : 'common.boolean.no');
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const getFieldLabel = (field, translate, fieldsSource, formatFieldLabel) => {
    const customLabel = formatFieldLabel?.(field, translate);
    if (customLabel) {
        return customLabel;
    }

    const leaf = String(field).split('.').pop();
    if (!fieldsSource) {
        return leaf;
    }

    return translate(`${fieldsSource}.${leaf}`, { _: leaf });
};

const ChangesField = ({ record, fieldsSource, formatFieldLabel, formatFieldChanges }) => {
    const translate = useTranslate();
    const entries = Object.entries(record?.changes ?? {});
    const rows = entries.flatMap(([field, diff]) => {
        const customLines = formatFieldChanges?.(field, diff, translate);

        if (Array.isArray(customLines)) {
            return customLines.map(line => ({
                key: `${field}-${line.key}`,
                label: line.label,
                oldValue: line.old,
                newValue: line.new,
            }));
        }

        return [
            {
                key: field,
                label: getFieldLabel(field, translate, fieldsSource, formatFieldLabel),
                oldValue: diff?.old,
                newValue: diff?.new,
            },
        ];
    });

    if (rows.length === 0) {
        return <Typography variant='body2'>—</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {rows.map(row => (
                <Typography key={row.key} variant='body2' sx={{ overflowWrap: 'anywhere' }}>
                    <Typography component='span' variant='body2' sx={{ fontWeight: 600 }}>
                        {row.label}:
                    </Typography>
                    {`${formatChangeValue(row.oldValue, translate)} -> ${formatChangeValue(row.newValue, translate)}`}
                </Typography>
            ))}
        </Box>
    );
};

export const ChangeHistory = ({ resource, recordId, fieldsSource, formatFieldLabel, formatFieldChanges }) => {
    const translate = useTranslate();
    const { isLoading, permissions } = usePermissions();
    const [isOpen, setIsOpen] = useState(false);

    if (isLoading || !(permissions?.isAdmin || permissions?.isBok)) return null;

    return (
        <StyledChangeHistory>
            <CollapsibleSection title={translate('common.changeHistory.title')} isOpen={isOpen} onToggle={() => setIsOpen(open => !open)}>
                <SimpleList
                    resource={`${resource}/${recordId}/history`}
                    sort={{ field: 'createdAt', order: 'DESC' }}
                    storeKey={`${resource}-history-${recordId}`}
                    perPage={20}
                    disablePagination
                    empty={
                        <NotificationBox variant='info' className='large'>
                            {translate('common.changeHistory.noHistory')}
                        </NotificationBox>
                    }
                >
                    <Datagrid body={<CustomDatagridBody />} bulkActionButtons={false} sort={false}>
                        <FunctionField
                            source='action'
                            label={translate('common.changeHistory.action')}
                            sortable={false}
                            render={record => {
                                const actionKey = record.action === 'update' ? 'edit' : record.action;
                                return translate(`common.changeHistory.actions.${actionKey}`, { _: record.action ?? '—' });
                            }}
                        />
                        <FunctionField
                            source='changes'
                            cellClassName='column-changes'
                            label={translate('common.changeHistory.changes')}
                            sortable={false}
                            render={record => (
                                <ChangesField
                                    record={record}
                                    fieldsSource={fieldsSource}
                                    formatFieldLabel={formatFieldLabel}
                                    formatFieldChanges={formatFieldChanges}
                                />
                            )}
                        />
                        <DateField source='createdAt' label={translate('common.changeHistory.createdAt')} showTime sortable={false} />
                        <ReferenceField
                            source='createdBy'
                            reference='user'
                            label={translate('common.changeHistory.createdBy')}
                            sortable={false}
                            link={false}
                            emptyText={translate('common.changeHistory.system')}
                        >
                            <FunctionField render={record => `${record.firstName} ${record.lastName} (${record.email})`} />
                        </ReferenceField>
                    </Datagrid>
                </SimpleList>
            </CollapsibleSection>
        </StyledChangeHistory>
    );
};

ChangesField.propTypes = {
    record: PropTypes.shape({
        changes: PropTypes.object,
    }),
    fieldsSource: PropTypes.string,
    formatFieldLabel: PropTypes.func,
    formatFieldChanges: PropTypes.func,
};

ChangeHistory.propTypes = {
    resource: PropTypes.string.isRequired,
    recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fieldsSource: PropTypes.string,
    formatFieldLabel: PropTypes.func,
    formatFieldChanges: PropTypes.func,
};
